<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, asList } from '../api/client'
import {
  normalizeBooking,
  normalizeResource,
  options,
  resourceTypeLabels,
  serializeBooking,
} from '../api/mappers'
import { formatDateTime, formatMoney } from '../utils'
import AppIcon from '../components/AppIcon.vue'
import BookingDialog from '../components/BookingDialog.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import StatusBadge from '../components/StatusBadge.vue'
import { useToast } from '../composables/useToast'

const route = useRoute()
const router = useRouter()
const { show } = useToast()

const bookings = ref([])
const resources = ref([])
const loading = ref(true)
const saving = ref(false)
const cancelling = ref(false)
const dialogOpen = ref(false)
const editingBooking = ref(null)
const bookingToCancel = ref(null)
const search = ref('')
const filters = reactive({
  dateFrom: '',
  dateTo: '',
  resourceType: '',
  resourceId: '',
  status: '',
})

const activeFilterCount = computed(() => Object.values(filters).filter(Boolean).length)
const visibleBookings = computed(() => {
  const term = search.value.trim().toLocaleLowerCase('ru')
  if (!term) return bookings.value
  if (/^#\d+$/.test(term)) return bookings.value.filter((booking) => String(booking.id) === term.slice(1))
  return bookings.value.filter((booking) => [
    booking.guestName,
    booking.guestPhone,
    booking.guestEmail,
    booking.resource.name,
    String(booking.id),
  ].some((value) => String(value || '').toLocaleLowerCase('ru').includes(term)))
})

function enrichBooking(raw) {
  const booking = normalizeBooking(raw)
  if (!booking.resource?.id) {
    const resource = resources.value.find((item) => String(item.id) === String(booking.resourceId))
    if (resource) booking.resource = resource
  }
  return booking
}

async function loadResources() {
  const payload = await api.resources.list()
  resources.value = asList(payload, ['resources']).map(normalizeResource)
}

async function loadBookings() {
  loading.value = true
  try {
    if (!resources.value.length) await loadResources()
    const payload = await api.bookings.listAll({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      resource_type: filters.resourceType,
      resource_id: filters.resourceId,
      status: filters.status,
    })
    bookings.value = asList(payload, ['bookings'])
      .map(enrichBooking)
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
  } catch (error) {
    show(error.message || 'Не удалось загрузить бронирования.', 'error')
  } finally {
    loading.value = false
  }
}

function resetFilters() {
  Object.assign(filters, { dateFrom: '', dateTo: '', resourceType: '', resourceId: '', status: '' })
  search.value = ''
  loadBookings()
}

function openCreate() {
  editingBooking.value = null
  dialogOpen.value = true
}

function openEdit(booking) {
  editingBooking.value = booking
  dialogOpen.value = true
}

function closeDialog() {
  if (!saving.value) dialogOpen.value = false
}

async function saveBooking(form) {
  saving.value = true
  try {
    const payload = serializeBooking(form)
    if (editingBooking.value) {
      await api.bookings.update(editingBooking.value.id, payload)
      show('Изменения в бронировании сохранены.')
    } else {
      await api.bookings.create(payload)
      show('Бронирование создано.')
    }
    dialogOpen.value = false
    await loadBookings()
  } catch (error) {
    show(error.message || 'Не удалось сохранить бронирование.', 'error')
  } finally {
    saving.value = false
  }
}

async function cancelBooking() {
  if (!bookingToCancel.value) return
  cancelling.value = true
  try {
    await api.bookings.cancel(bookingToCancel.value.id)
    show(`Бронирование №${bookingToCancel.value.id} отменено.`)
    bookingToCancel.value = null
    await loadBookings()
  } catch (error) {
    show(error.message || 'Не удалось отменить бронирование.', 'error')
  } finally {
    cancelling.value = false
  }
}

onMounted(async () => {
  await loadBookings()
  if (route.query.booking) {
    search.value = `#${route.query.booking}`
  }
  if (route.query.new === '1') {
    openCreate()
    const query = { ...route.query }
    delete query.new
    router.replace({ query })
  }
})
</script>

<template>
  <div class="page bookings-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">Гости и размещение</span>
        <h1>Бронирования</h1>
        <p class="page-subtitle">Планируйте заезды, оплату и загрузку объектов.</p>
      </div>
      <button type="button" class="button button--primary" @click="openCreate">
        <AppIcon name="plus" :size="18" />
        Новая бронь
      </button>
    </header>

    <section class="filter-bar" aria-label="Фильтры бронирований">
      <label class="search-field">
        <AppIcon name="search" :size="18" />
        <span class="sr-only">Поиск бронирований</span>
        <input v-model="search" type="search" placeholder="Гость, телефон, объект или №" />
      </label>
      <label class="compact-field">
        <span>С</span>
        <input v-model="filters.dateFrom" type="date" />
      </label>
      <label class="compact-field">
        <span>По</span>
        <input v-model="filters.dateTo" type="date" />
      </label>
      <label class="compact-field">
        <span class="sr-only">Тип объекта</span>
        <select v-model="filters.resourceType">
          <option value="">Все типы</option>
          <option v-for="type in options.resourceTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
        </select>
      </label>
      <label class="compact-field">
        <span class="sr-only">Объект</span>
        <select v-model="filters.resourceId">
          <option value="">Все объекты</option>
          <option v-for="resource in resources" :key="resource.id" :value="resource.id">{{ resource.name }}</option>
        </select>
      </label>
      <label class="compact-field">
        <span class="sr-only">Статус</span>
        <select v-model="filters.status">
          <option value="">Все статусы</option>
          <option v-for="status in options.bookingStatuses" :key="status.value" :value="status.value">{{ status.label }}</option>
        </select>
      </label>
      <button type="button" class="button button--secondary button--compact" @click="loadBookings">Показать</button>
      <button v-if="activeFilterCount" type="button" class="text-button" @click="resetFilters">Сбросить · {{ activeFilterCount }}</button>
    </section>

    <LoadingState v-if="loading" />

    <section v-else class="data-section" aria-labelledby="bookings-list-heading">
      <header class="section-header section-header--compact">
        <div>
          <h2 id="bookings-list-heading">Список броней</h2>
          <p>{{ visibleBookings.length }} {{ visibleBookings.length === 1 ? 'запись' : 'записей' }}</p>
        </div>
      </header>

      <div v-if="visibleBookings.length" class="table-scroll">
        <table class="data-table bookings-table">
          <caption class="sr-only">Список бронирований</caption>
          <thead>
            <tr>
              <th scope="col">Гость</th>
              <th scope="col">Объект</th>
              <th scope="col">Период</th>
              <th scope="col">Статус</th>
              <th scope="col">Оплата</th>
              <th scope="col"><span class="sr-only">Действия</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="booking in visibleBookings" :key="booking.id" :class="{ 'row--muted': booking.status === 'cancelled' }">
              <td data-label="Гость">
                <strong>{{ booking.guestName }}</strong>
                <span class="cell-secondary">
                  <a v-if="booking.guestPhone" :href="`tel:${booking.guestPhone}`">{{ booking.guestPhone }}</a>
                  <span v-else>№{{ booking.id }}</span>
                  <span> · {{ booking.guestsCount }} чел.</span>
                </span>
              </td>
              <td data-label="Объект">
                <strong>{{ booking.resource.name }}</strong>
                <span class="cell-secondary">{{ resourceTypeLabels[booking.resource.resourceType] }}</span>
              </td>
              <td data-label="Период">
                <strong>{{ formatDateTime(booking.startsAt) }}</strong>
                <span class="cell-secondary">до {{ formatDateTime(booking.endsAt) }}</span>
              </td>
              <td data-label="Статус"><StatusBadge :status="booking.status" /></td>
              <td data-label="Оплата">
                <strong>{{ formatMoney(booking.totalAmount) }}</strong>
                <span v-if="booking.prepaidAmount != null" class="cell-secondary">внесено {{ formatMoney(booking.prepaidAmount) }}</span>
              </td>
              <td class="row-actions" data-label="Действия">
                <button type="button" class="icon-button icon-button--bordered" title="Редактировать" :aria-label="`Редактировать бронь ${booking.guestName}`" @click="openEdit(booking)">
                  <AppIcon name="edit" :size="17" />
                </button>
                <button
                  v-if="booking.status !== 'cancelled' && booking.status !== 'completed'"
                  type="button"
                  class="icon-button icon-button--danger"
                  title="Отменить"
                  :aria-label="`Отменить бронь ${booking.guestName}`"
                  @click="bookingToCancel = booking"
                >
                  <AppIcon name="ban" :size="17" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <EmptyState v-else title="Брони не найдены" text="Измените фильтры или создайте новое бронирование.">
        <button type="button" class="button button--primary" @click="openCreate">Создать бронь</button>
      </EmptyState>
    </section>

    <BookingDialog
      :open="dialogOpen"
      :booking="editingBooking"
      :resources="resources"
      :saving="saving"
      @close="closeDialog"
      @save="saveBooking"
    />

    <ConfirmDialog
      :open="Boolean(bookingToCancel)"
      title="Отменить бронирование?"
      :text="`Бронь для гостя «${bookingToCancel?.guestName || ''}» будет отмечена как отменённая. Объект снова станет доступен для бронирования.`"
      confirm-label="Отменить бронь"
      :pending="cancelling"
      @close="bookingToCancel = null"
      @confirm="cancelBooking"
    />
  </div>
</template>
